import { Box, Divider, FormField, ImageViewer, Input } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext } from 'react';

import {
  ModalServiceContext,
  PluginsEventsContext,
  RicosContext,
  ZIndexContext,
} from './../../ricos-context';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import { logInvariant } from './../../_shared/log-invariant';
import { getImageSrc } from './../../wix-rich-content-common/lib/imageUtils';
import {
  Panel,
  PanelHeader,
  ToggleButton,
  usePanelContainerElement,
} from './../../ui-components';

import { useNodeAttrs } from '../../_shared/use-node-attrs';
import {
  AUDIO_ACTION_NAMES,
  AUDIO_BI_VALUES,
  AUDIO_SETTINGS_MODAL_ID,
  AUDIO_TYPES,
} from '../consts';
import {
  SETTINGS_AUDIO_COVER_IMAGE,
  SETTINGS_AUDIO_NAME_INPUT,
  SETTINGS_AUTHOR_NAME_INPUT,
  SETTINGS_DOWNLOAD_TOGGLE,
  SETTINGS_MODAL,
} from '../data-hooks';
import classes from '../statics/styles/settings-modal-scss';
import { AUDIO_TYPE } from '../types';

export type AudioSettingsModalProps = {
  nodeId: string;
  onFileSelect: (index?: number) => void;
};

export const AudioSettingsModal: FC<AudioSettingsModalProps> = ({ nodeId, onFileSelect }) => {
  const { t, environment } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext) || {};
  const pluginsEvents = useContext(PluginsEventsContext);
  const zIndexService = useContext(ZIndexContext);
  const containerEl = usePanelContainerElement();
  const { patchPmNodeAttrs, pmNodeAttrs } = useNodeAttrs(nodeId, 'AUDIO');
  const logBi = useBiNext({ pluginId: 'audio', modalId: AUDIO_SETTINGS_MODAL_ID, nodeId });

  if (!pmNodeAttrs) {
    logInvariant(new Error('Audio settings: node attrs not found'));
    return null;
  }

  const closeModal = () => {
    modalService.closeModal(AUDIO_SETTINGS_MODAL_ID);
  };

  const imgSrc =
    pmNodeAttrs.coverImage &&
    getImageSrc(
      { file_name: pmNodeAttrs.coverImage.src?.id || pmNodeAttrs.coverImage?.src.id },
      undefined,
      {
        requiredWidth: 120,
        requiredHeight: 120,
        requiredQuality: 70,
      }
    );

  const onInputBlur = (actionName: $TSFixMe, value: $TSFixMe) => {
    pluginsEvents.publishPluginChangeSettings?.({
      nodeId,
      pluginId: AUDIO_TYPE,
      actionName,
      value,
    });
  };

  return (
    <Panel onCloseButtonClick={closeModal} dataHook={SETTINGS_MODAL}>
      <PanelHeader title={t('AudioPlugin_Settings_Header')} />

      <Box direction="vertical" padding="SP3" gap="SP3" overflow="auto">
        <FormField label={t('AudioPlugin_Settings_AudioName_Label')}>
          <Input
            placeholder={t('AudioPlugin_Settings_AudioName_Label_Placeholder')}
            value={pmNodeAttrs.name ?? undefined}
            onBlur={e => {
              onInputBlur(AUDIO_ACTION_NAMES.changeTitle, e.target.value);
            }}
            onChange={e => {
              patchPmNodeAttrs({
                name: e.target.value,
              });

              logBi({ dataHook: SETTINGS_AUDIO_NAME_INPUT, value: e.target.value });

              pluginsEvents.publishMediaPluginsDetails?.({
                pluginId: AUDIO_TYPE,
                nodeId,
                creator: pmNodeAttrs.authorName ?? undefined,
                title: e.target.value,
                track_duration: pmNodeAttrs.audio?.duration?.toString(),
                type: AUDIO_TYPES.custom,
                url: pmNodeAttrs.audio?.src?.id ?? '',
              });
            }}
            dataHook={SETTINGS_AUDIO_NAME_INPUT}
            size="small"
          />
        </FormField>
        <FormField label={t('AudioPlugin_Settings_AuthorName_Label')}>
          <Input
            placeholder={t('AudioPlugin_Settings_AuthorName_Label_Placeholder')}
            value={pmNodeAttrs.authorName ?? undefined}
            onBlur={e => {
              onInputBlur(AUDIO_ACTION_NAMES.changeCreator, e.target.value);
            }}
            onChange={e => {
              patchPmNodeAttrs({ authorName: e.target.value });

              logBi({ dataHook: SETTINGS_AUTHOR_NAME_INPUT, value: e.target.value });

              pluginsEvents.publishMediaPluginsDetails?.({
                pluginId: AUDIO_TYPE,
                nodeId,
                creator: e.target.value,
                title: pmNodeAttrs.name ?? undefined,
                track_duration: pmNodeAttrs.audio?.duration?.toString(),
                type: AUDIO_TYPES.custom,
                url: pmNodeAttrs.audio?.src?.id ?? '',
              });
            }}
            dataHook={SETTINGS_AUTHOR_NAME_INPUT}
            size="small"
          />
        </FormField>
        <FormField label={t('AudioPlugin_Settings_CoverImage_Label')}>
          <ImageViewer
            className={environment === 'liveSite' ? classes.imageViewer : undefined}
            dataHook={SETTINGS_AUDIO_COVER_IMAGE}
            imageUrl={imgSrc}
            width={120}
            height={120}
            removeRoundedBorders={environment === 'liveSite'}
            onAddImage={() => {
              pluginsEvents.publishPluginChangeSettings?.({
                pluginId: AUDIO_TYPE,
                nodeId,
                actionName: AUDIO_ACTION_NAMES.changeCover,
                value: AUDIO_BI_VALUES.YES,
              });
              onFileSelect();
            }}
            onUpdateImage={() => {
              pluginsEvents.publishPluginChangeSettings?.({
                pluginId: AUDIO_TYPE,
                nodeId,
                actionName: AUDIO_ACTION_NAMES.changeCover,
                value: AUDIO_BI_VALUES.YES,
              });
              onFileSelect(0);
            }}
            onRemoveImage={() => {
              patchPmNodeAttrs({ coverImage: null });

              logBi({ dataHook: SETTINGS_AUDIO_COVER_IMAGE });

              pluginsEvents.publishMediaPluginsDetails?.({
                pluginId: AUDIO_TYPE,
                nodeId,
                creator: pmNodeAttrs.authorName ?? undefined,
                title: pmNodeAttrs.name ?? undefined,
                track_duration: pmNodeAttrs.audio?.duration?.toString(),
                type: AUDIO_TYPES.custom,
                url: pmNodeAttrs.audio?.src?.id ?? '',
              });

              pluginsEvents.publishPluginChangeSettings?.({
                nodeId,
                pluginId: AUDIO_TYPE,
                actionName: AUDIO_ACTION_NAMES.changeCover,
                value: AUDIO_BI_VALUES.NO,
              });
            }}
            tooltipProps={{
              appendTo: containerEl,
              zIndex: zIndexService.getZIndex('TOOLTIP'),
            }}
          />
        </FormField>
        <Divider />
        <ToggleButton
          checked={!pmNodeAttrs.disableDownload}
          title={t('AudioPlugin_Settings_AudioCanBeDownloaded_Label')}
          size="small"
          dataHook={SETTINGS_DOWNLOAD_TOGGLE}
          onChange={() => {
            const disableDownload = !pmNodeAttrs.disableDownload;

            patchPmNodeAttrs({ disableDownload });

            logBi({ dataHook: SETTINGS_DOWNLOAD_TOGGLE, value: !!pmNodeAttrs.disableDownload });

            pluginsEvents.publishPluginChangeSettings?.({
              pluginId: AUDIO_TYPE,
              nodeId,
              actionName: AUDIO_ACTION_NAMES.downloadAudio,
              value: disableDownload ? AUDIO_BI_VALUES.YES : AUDIO_BI_VALUES.NO,
            });

            pluginsEvents.publishMediaPluginsDetails?.({
              pluginId: AUDIO_TYPE,
              nodeId,
              creator: pmNodeAttrs.authorName ?? undefined,
              title: pmNodeAttrs.name ?? undefined,
              track_duration: pmNodeAttrs.audio?.duration?.toString(),
              type: AUDIO_TYPES.custom,
              url: pmNodeAttrs.audio?.src?.id ?? '',
            });
          }}
          infoContent={t('AudioPlugin_Settings_AudioCanBeDownloaded_Tooltip')}
          tooltipAppendTo={containerEl}
        />
      </Box>
    </Panel>
  );
};
